import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { huntingArea } from '../data/mock';
import { Card } from './ui/card';
import { 
  MapPin, Navigation, TreePine, PawPrint, AlertTriangle, Shield,
  Bird, Bug, Cat, Dog, Fish, Rabbit, Squirrel, Leaf, Mountain,
  Compass, Target, Eye, Heart, Star
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultHuntingAreaImage = 'https://images.unsplash.com/photo-1534218238612-bace67b05bf2?w=800&h=500&fit=crop';

// Icon mapping
const iconMap = {
  PawPrint, TreePine, AlertTriangle, Shield, Bird, Bug, Cat, Dog,
  Fish, Rabbit, Squirrel, Leaf, Mountain, Compass, Target, Eye, Heart, Star, MapPin, Navigation
};

const getIconComponent = (iconName) => {
  return iconMap[iconName] || PawPrint;
};

const getColorClasses = (color) => {
  const colors = {
    emerald: { bg: 'bg-emerald-600/20', text: 'text-emerald-500', border: 'border-emerald-500/30' },
    amber: { bg: 'bg-amber-600/20', text: 'text-amber-500', border: 'border-amber-500/30' },
    orange: { bg: 'bg-orange-600/20', text: 'text-orange-500', border: 'border-orange-500/30' },
    red: { bg: 'bg-red-600/20', text: 'text-red-500', border: 'border-red-500/30' },
    blue: { bg: 'bg-blue-600/20', text: 'text-blue-500', border: 'border-blue-500/30' },
    purple: { bg: 'bg-purple-600/20', text: 'text-purple-500', border: 'border-purple-500/30' },
    green: { bg: 'bg-green-700/20', text: 'text-green-500', border: 'border-green-500/30' },
    yellow: { bg: 'bg-yellow-600/20', text: 'text-yellow-500', border: 'border-yellow-500/30' },
  };
  return colors[color] || colors.emerald;
};

const HuntingArea = () => {
  const { t, language } = useLanguage();
  const areaData = huntingArea[language] || huntingArea.sl;
  const [huntingAreaImage, setHuntingAreaImage] = useState(null);
  const [wildlifeSections, setWildlifeSections] = useState([]);
  const [texts, setTexts] = useState({});
  const [loading, setLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch content (images)
        const contentResponse = await axios.get(`${API}/content`);
        setHuntingAreaImage(contentResponse.data?.hunting_area_image || defaultHuntingAreaImage);
        setImageLoading(false);

        // Fetch wildlife sections
        const wildlifeResponse = await axios.get(`${API}/wildlife`);
        if (wildlifeResponse.data && wildlifeResponse.data.length > 0) {
          setWildlifeSections(wildlifeResponse.data);
        } else {
          await axios.post(`${API}/seed-wildlife`);
          const retryResponse = await axios.get(`${API}/wildlife`);
          setWildlifeSections(retryResponse.data);
        }

        // Fetch texts
        const textsResponse = await axios.get(`${API}/texts`);
        setTexts(textsResponse.data || {});

      } catch (error) {
        console.error('Error fetching hunting area data:', error);
        setHuntingAreaImage(defaultHuntingAreaImage);
        setImageLoading(false);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getTextValue = (key, defaultSl, defaultEn) => {
    if (texts[key]) {
      return language === 'sl' ? texts[key].sl : texts[key].en;
    }
    return language === 'sl' ? defaultSl : defaultEn;
  };

  return (
    <section id="hunting-area" className="py-24 bg-stone-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-emerald-400 mb-4">
            <Navigation className="w-5 h-5" />
            <span className="text-sm font-semibold tracking-wider uppercase">
              {t.huntingArea.title}
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t.huntingArea.title}
          </h2>
          <p className="text-lg text-stone-400 max-w-3xl mx-auto">
            {getTextValue(
              'hunting_area_description',
              'Naše lovišče obsega 4.911,34 hektarjev skupne površine, od tega 4.703,30 ha lovne površine. Raznolik teren od gostih gozdov do travnikov in mokrišč je dom bogati favni.',
              'Our hunting ground covers 4,911.34 hectares of total area, of which 4,703.30 ha is hunting area. The diverse terrain from dense forests to meadows and wetlands is home to rich fauna.'
            )}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Map Image with stats */}
          <div className="relative rounded-2xl overflow-hidden h-full min-h-[300px]">
            {imageLoading ? (
              <div className="w-full h-full min-h-[300px] bg-stone-800 animate-pulse" />
            ) : (
              <img
                src={huntingAreaImage}
                alt="Lovišče Lovske družine Rakek"
                className="w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/30 to-transparent" />
            
            {/* Stats overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-stone-900/80 backdrop-blur border-stone-700/50 p-4">
                  <p className="text-emerald-400 text-2xl font-bold">4.911 ha</p>
                  <p className="text-stone-400 text-sm">
                    {language === 'sl' ? 'Skupna površina' : 'Total Area'}
                  </p>
                </Card>
                <Card className="bg-stone-900/80 backdrop-blur border-stone-700/50 p-4">
                  <p className="text-emerald-400 text-2xl font-bold">4.703 ha</p>
                  <p className="text-stone-400 text-sm">
                    {language === 'sl' ? 'Lovna površina' : 'Hunting Area'}
                  </p>
                </Card>
              </div>
            </div>
          </div>

          {/* Location Info */}
          <div className="space-y-6">
            <Card className="bg-stone-800/50 border-stone-700/50 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-emerald-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {language === 'sl' ? 'Lokacija' : 'Location'}
                  </h3>
                  <p className="text-stone-400">
                    {language === 'sl' 
                      ? 'Lovišče se nahaja na območju Notranjske, v jugozahodnem delu Slovenije.'
                      : 'The hunting ground is located in the Notranjska region, in the southwestern part of Slovenia.'}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="bg-stone-800/50 border-stone-700/50 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <TreePine className="w-6 h-6 text-emerald-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {language === 'sl' ? 'Teren' : 'Terrain'}
                  </h3>
                  <p className="text-stone-400">
                    {language === 'sl' 
                      ? 'Raznolik kraški teren z gozdovi, travniki in kraškimi pojavi.'
                      : 'Diverse karst terrain with forests, meadows and karst features.'}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="bg-stone-800/50 border-stone-700/50 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Navigation className="w-6 h-6 text-emerald-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {language === 'sl' ? 'Nadmorska višina' : 'Altitude'}
                  </h3>
                  <p className="text-stone-400">
                    {language === 'sl' 
                      ? '500 - 1.200 m nadmorske višine'
                      : '500 - 1,200 m above sea level'}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Wildlife Section */}
        <div className="mb-12">
          <h3 className="text-3xl font-bold text-white text-center mb-4">
            {language === 'sl' ? 'Divjad v našem lovišču' : 'Wildlife in Our Hunting Ground'}
          </h3>
          <p className="text-stone-400 text-center max-w-3xl mx-auto mb-10">
            {getTextValue(
              'wildlife_intro',
              'Lovišče Rakek se razteza čez razgibano pokrajino, ki nudi zavetje številnim vrstam divjadi. Naše upravljanje z loviščem temelji na spoštovanju naravnih procesov.',
              'The Rakek hunting ground stretches across a diverse landscape that provides shelter to numerous wildlife species. Our management is based on respect for natural processes.'
            )}
          </p>
        </div>

        {/* Wildlife Sections from API */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {wildlifeSections.map((section) => {
              const IconComponent = getIconComponent(section.icon);
              const colorClasses = getColorClasses(section.color);
              
              return (
                <Card 
                  key={section.id} 
                  className={`bg-stone-800/50 border-stone-700/50 p-6 hover:${colorClasses.border} transition-colors`}
                  data-testid={`wildlife-section-${section.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 ${colorClasses.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <IconComponent className={`w-7 h-7 ${colorClasses.text}`} />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-white mb-2">
                        {language === 'sl' ? section.title_sl : section.title_en}
                      </h4>
                      <p className="text-stone-400 leading-relaxed">
                        {language === 'sl' ? section.content_sl : section.content_en}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Commitment Footer */}
        <Card className="bg-emerald-900/20 border-emerald-600/30 p-8 text-center">
          <p className="text-emerald-300 text-lg leading-relaxed">
            {getTextValue(
              'wildlife_commitment',
              'Lovci LD Rakek si bomo še naprej prizadevali za strokovno spremljanje vseh vrst divjadi in aktivno reševanje izzivov, ki jih prinašajo spremembe v naravnem okolju.',
              'The hunters of LD Rakek will continue to strive for professional monitoring of all wildlife species and actively address the challenges brought by changes in the natural environment.'
            )}
          </p>
        </Card>
      </div>
    </section>
  );
};

export default HuntingArea;
